import { useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useApi, useLoad, errMsg, fmtDate, FOODS } from "@/lib/api";
import { PageHead, card, PatientSelect, Empty } from "@/components/admin/kit";
import { MedicineForm, M0 } from "@/components/admin/MedicineForm";
import { useCapGuard } from "@/components/admin/CapGuard";

export function MedList({ patientId, onEdit }) {
  const api = useApi();
  const [list, reload] = useLoad(patientId ? `/medicines-view?patient_id=${patientId}` : null);
  if (!list) return null;
  if (!list.length) return <Empty>No medicines yet.</Empty>;
  return (
    <div className="grid gap-2">
      {list.map((m) => (
        <div key={m.id} className={`rounded-2xl border p-3 text-sm ${m.active ? "border-[var(--lav-3)] bg-[var(--lav)]/50" : "border-dashed opacity-60"}`} data-testid={`medicine-row-${m.id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <b>{m.name}</b><span className="font-te text-[var(--violet)]">{m.name_voice}</span><span className="text-muted-foreground">{m.strength}</span>
            <span className="rounded-full bg-white px-2 text-xs">{m.purpose}{m.sensitive && " · sensitive"}</span>
            {!m.active && <span className="rounded-full bg-gray-200 px-2 text-xs">ended / not started</span>}
            <div className="ml-auto flex gap-1">
              <Button size="icon" variant="ghost" onClick={() => onEdit(m)} data-testid={`edit-medicine-${m.id}`}><Pencil size={14} /></Button>
              <Button size="icon" variant="ghost" onClick={async () => { if (window.confirm("Remove this medicine?")) { try { await api.delete(`/medicines/${m.id}`); reload(); } catch (e) { toast.error(errMsg(e)); } } }} data-testid={`delete-medicine-${m.id}`}><Trash2 size={14} /></Button>
            </div>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {m.dose_amount} {m.form} · {m.slots.join(", ")} · {FOODS[m.food]} · from {fmtDate(m.start_date)} · {m.long_term ? "long-term" : `${m.days} days`}
            {m.refill_date && <> · <span className={m.days_left <= 7 ? "font-semibold text-orange-700" : ""}>refill {fmtDate(m.refill_date)} ({m.days_left} days)</span></>}
          </p>
        </div>
      ))}
    </div>
  );
}

export default function Medicines() {
  const api = useApi();
  const [pid, setPid] = useState("");
  const [med, setMed] = useState(null);
  const [k, setK] = useState(0);
  const { guard, dialog } = useCapGuard();
  const save = async (body) => {
    if (!(await guard(body.patient_id, [{ ...body, id: med.id }]))) return;
    try { med.id ? await api.put(`/medicines/${med.id}`, body) : await api.post("/medicines", body); setMed(null); setK((x) => x + 1); toast.success("Medicine saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="medicines-page">
      {dialog}
      <PageHead title="Medicines" sub="Add medicines by hand here, or pre-fill them from a prescription scan.">
        <div className="w-64"><PatientSelect value={pid} onChange={setPid} /></div>
        <Button disabled={!pid} onClick={() => setMed(M0(pid))} data-testid="medicines-add-btn"><Plus size={16} /> Add medicine</Button>
      </PageHead>
      <div className={card}>{pid ? <MedList key={`${pid}-${k}`} patientId={pid} onEdit={setMed} /> : <Empty>Choose a patient to see their medicines.</Empty>}</div>
      <Dialog open={!!med} onOpenChange={(o) => !o && setMed(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl"><DialogHeader><DialogTitle>{med?.id ? "Edit medicine" : "Add medicine"}</DialogTitle></DialogHeader>
          {med && <MedicineForm init={{ ...med, hospital_id: med.hospital_id || "", doctor_id: med.doctor_id || "" }} onSave={save} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
